import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Home from "./components/Home";
import BlogIndex from "./components/blog/BlogIndex";
import BlogPost from "./components/blog/BlogPost";
import NotFound from "./components/blog/NotFound";
import PreLoader from "./components/PreLoader";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { getLenisInstance, setLenisInstance } from "./lenisInstance";
import { markPreloaderPlayed, shouldPlayPreloader } from "./preloaderState";
import { RevealProvider } from "./revealContext";

const Site = () => {
  const location = useLocation();
  const [selectedPage, setSelectedPage] = useState(
    location.pathname.startsWith("/blog") ? "blog" : "home",
  );
  // Evaluated once, on mount: the preloader belongs to a page load, not a route
  // change, so client-side navigation never brings it back.
  const [loading, setLoading] = useState(() =>
    shouldPlayPreloader(location.pathname),
  );

  const finishLoading = useCallback(() => {
    markPreloaderPlayed();
    setLoading(false);
  }, []);

  /* KEEP THE NAV HIGHLIGHT IN SYNC WITH THE URL */
  useEffect(() => {
    if (location.pathname.startsWith("/blog")) setSelectedPage("blog");
    else if (location.pathname === "/") setSelectedPage("home");
  }, [location.pathname]);

  /* SCROLL POSITION ON NAVIGATION
     Declared before the Lenis effect so a new route starts at the top before a
     fresh Lenis instance reads the scroll position. Skipped on first mount, so
     a cold load of "/" behaves exactly as it always has. */
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    window.scrollTo(0, 0);

    // Arriving from a navbar anchor on another page: scroll to that section
    // once the landing has painted and Lenis has been recreated.
    const anchor = location.state?.scrollTo;
    if (!anchor || location.pathname !== "/") return;

    let cancelled = false;
    let timer;

    const scrollToAnchor = () => {
      const lenis = getLenisInstance();
      if (anchor === "home") {
        if (lenis) lenis.scrollTo("top");
        else window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const target = document.getElementById(anchor);
      if (!target) return;
      if (lenis) lenis.scrollTo(target);
      else target.scrollIntoView({ behavior: "smooth" });
    };

    // The landing's images decide where #projects ends up, so scrolling on the
    // first frame aims at a stale offset. Wait for the page height to hold
    // still (or give up after a beat) before aiming.
    let lastHeight = -1;
    let stableSamples = 0;
    const waitForStableLayout = (elapsed) => {
      if (cancelled) return;
      const height = document.body.scrollHeight;
      stableSamples = height === lastHeight ? stableSamples + 1 : 0;
      lastHeight = height;

      if (stableSamples < 2 && elapsed < 1500) {
        timer = setTimeout(() => waitForStableLayout(elapsed + 100), 100);
        return;
      }
      scrollToAnchor();
    };

    waitForStableLayout(0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [location.pathname, location.key, location.state]);

  /* NOTHING REACHABLE BEHIND THE CURTAIN
     The site is mounted underneath the intro overlay so its assets can warm up,
     which means the document has its full height and its full set of links and
     landmarks while the intro plays. Lenis is not running yet, but native
     scrolling is, and a visitor who touches the wheel would silently scroll the
     page they cannot see; likewise Tab would walk a navbar hidden under an
     opaque curtain and Enter would navigate off it. So the content is pinned,
     made inert and hidden from assistive tech for exactly as long as the
     overlay is up - the overlay itself sits outside this subtree and stays
     reachable and announced. React 18 has no `inert` prop, hence the ref. */
  const contentRef = useRef(null);
  useEffect(() => {
    if (!loading) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const content = contentRef.current;
    if (content) {
      content.setAttribute("inert", "");
      content.setAttribute("aria-hidden", "true");
    }
    return () => {
      document.body.style.overflow = previous;
      if (content) {
        content.removeAttribute("inert");
        content.removeAttribute("aria-hidden");
      }
    };
  }, [loading]);

  /* SMOOTH SCROLLING - one instance per route, torn down on the way out */
  useEffect(() => {
    if (loading) return;

    const lenis = new Lenis();
    setLenisInstance(lenis);

    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      setLenisInstance(null);
    };
  }, [loading, location.pathname]);

  /*DISPLAY
    The site renders underneath the intro rather than after it. That is the
    whole point of the refactor: mounting the landing while the overlay is up is
    what gets the WebGL context created, the shaders compiled and the dragon's
    buffers uploaded before anyone is looking. `RevealProvider` holds the
    entrance animations at their start until the overlay lifts, so what the
    visitor sees is unchanged. */
  return (
    <div className="app">
      <RevealProvider value={!loading}>
        <div ref={contentRef}>
          <Navbar
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
            key="navbar"
          />
          <Routes>
            <Route
              path="/"
              element={<Home setSelectedPage={setSelectedPage} />}
            />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </RevealProvider>
      <AnimatePresence>
        {loading && (
          <motion.div key="preloader">
            <PreLoader setLoading={finishLoading} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Site />
    </BrowserRouter>
  );
}

export default App;
